/* eslint-disable @typescript-eslint/indent */
import { Button, FormControl, FormErrorMessage, FormLabel, HStack, Input, Stack, Text, useToast, VStack } from "@chakra-ui/react"
import { isValid } from "date-fns"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import "react-calendar/dist/Calendar.css"
import "react-clock/dist/Clock.css"
import DateTimePicker from "react-datetime-picker"
import "react-datetime-picker/dist/DateTimePicker.css"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { UserSelector } from "../components"
import { useMarkUserAttendanceBulkInMutation } from "../graphql"

type MarkManualUserAttendanceBulkInFormValues = {
	userIds: string[]
	inTime: Date
	inRemarks: string
}
const validationSchema = yup.object<Record<keyof MarkManualUserAttendanceBulkInFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	userIds: yup.array().of(yup.string()).min(1).required().label("Users"),
	inTime: yup.date().required().label("In Time"),
	inRemarks: yup.string().required().label("In Remarks"),
})

export const MarkManualUserAttendanceBulkInForm: React.FC = () => {
	const toast = useToast()

	const [{ fetching }, markUserAttendanceBulkIn] = useMarkUserAttendanceBulkInMutation()

	const [isOptionSelected, setIsOptionSelected] = React.useState(false)
	const [isOtherSelected, setIsOtherSelected] = React.useState(false)

	const onSubmit = async (values: MarkManualUserAttendanceBulkInFormValues) => {
		const { data, error } = await markUserAttendanceBulkIn({
			input: {
				inRemarks: values.inRemarks,
				inTime: new Date(values.inTime),
				userIds: values.userIds,
			},
		})

		if (error) {
			toast({
				title: "Error",
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
				duration: 5000,
				isClosable: true,
			})
			return
		}

		if (data?.markUserAttendanceBulkIn) {
			//reset from
			formik.resetForm()
			toast({
				title: `${data?.markUserAttendanceBulkIn.length} users attendances marked in`,
				description: "Bulk attendance marked in successfully",
				status: "success",
				duration: 5000,
				isClosable: true,
			})
		}
	}

	const formik = useFormik<MarkManualUserAttendanceBulkInFormValues>({
		initialValues: {
			userIds: [],
			inTime: new Date(),
			inRemarks: "",
		},
		validationSchema: validationSchema,
		onSubmit,
	})

	const options = ["No electricity", "Internet issue", "Card Punch issue", "Machine Not wroking"]

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<Stack w="full" direction={{ base: "column", xl: "row" }}>
				<VStack w="full" align="stretch">
					<VStack w="full" maxW="lg" align="stretch" spacing={4}>
						<FormControl id="inTime">
							<FormLabel>In Time</FormLabel>
							<DateTimePicker onChange={(value) => isValid(value) && formik.setFieldValue("inTime", value)} value={formik.values.inTime} format="dd/MM/yyyy hh:mm a" maxDate={new Date()} clearIcon={null} />
						</FormControl>

						<FormControl id="inRemarks" isInvalid={!!formik.errors.inRemarks && formik.touched.inRemarks}>
							<FormLabel>In Remarks</FormLabel>
							<HStack justifyContent={"flex-start"} gap={5} wrap="wrap">
								{options.map((option, index) => (
									<Button
										variant={isOptionSelected ? (formik.values.inRemarks === option ? "solid" : "outline") : "outline"}
										colorScheme="primary"
										color={isOptionSelected ? (formik.values.inRemarks === option ? "white" : "primary.500") : "primary.500"}
										size="sm"
										p={2}
										key={index}
										onClick={() => {
											formik.setFieldValue("inRemarks", option)
											setIsOptionSelected(true)
										}}
									>
										{option}
									</Button>
								))}
								<Button
									variant={isOtherSelected ? "solid" : "outline"}
									colorScheme="primary"
									color={isOtherSelected ? "white" : "primary.500"}
									size="sm"
									p={2}
									onClick={() => {
										formik.setFieldValue("inRemarks", "")
										setIsOtherSelected(!isOtherSelected)

										formik.setFieldValue("inRemarks", "")
									}}
								>
									Other
								</Button>
							</HStack>
							<FormErrorMessage>{formik.errors.inRemarks}</FormErrorMessage>
						</FormControl>
						{isOtherSelected && (
							<FormControl id="inRemarks" isInvalid={!!formik.errors.inRemarks && formik.touched.inRemarks}>
								<FormLabel>In Remarks</FormLabel>
								<Input {...formik.getFieldProps("inRemarks")} />
								<FormErrorMessage>{formik.errors.inRemarks}</FormErrorMessage>
							</FormControl>
						)}
					</VStack>
				</VStack>
				<VStack w="full" align="stretch">
					<FormControl isInvalid={Boolean(formik.touched.userIds && formik.errors.userIds)}>
						<FormLabel fontWeight="bold">Users</FormLabel>

						<UserSelector value={formik.values.userIds} onUpdate={(userIds) => formik.setFieldValue("userIds", userIds)} />
						<Text fontSize="sm" color="error.400">
							{formik.errors.userIds}
						</Text>
					</FormControl>
					<Button type="submit" colorScheme="primary" isLoading={fetching}>
						Mark In
					</Button>
				</VStack>
			</Stack>
		</VStack>
	)
}
